import dayjs from 'dayjs'
import { endOfDay, INFINITY, isBookingPast, KIND_PERMANENT, KIND_SINGLE, toBookingRow } from '@/components/bookings/utils'

export const UPCOMING_DAYS = 14
export const RECENT_DAYS = 30

/**
 * Calendar Monday-Sunday of the week before the one `now` falls in.
 *
 * dayjs' startOf('week') follows the locale - Sunday on the default `en` - while the home
 * screen is specified as Mon-Sun, so the offset is computed rather than borrowed.
 */
export const lastWeekWindow = (now = dayjs()) => {
  // day(): 0 = Sunday .. 6 = Saturday, so Sunday sits 6 days after its own Monday
  const sinceMonday = (now.day() + 6) % 7
  const from = now.startOf('day').subtract(sinceMonday + 7, 'day')

  return { from, to: endOfDay(from.add(6, 'day')) }
}

/** Today through the next fortnight - what "upcoming bookings" means on this screen. */
export const upcomingWindow = (now = dayjs()) => ({
  from: now.startOf('day'),
  to: endOfDay(now.add(UPCOMING_DAYS, 'day'))
})

/** The last 30 days including today. */
export const recentWindow = (now = dayjs()) => ({
  from: now.startOf('day').subtract(RECENT_DAYS - 1, 'day'),
  to: endOfDay(now)
})

/**
 * Distinct calendar days a set of occurrences covers, clipped to the window.
 *
 * Fed from /desk-events rather than /desk-event-grouped: the grouped endpoint collapses a
 * recurring series into one row, while desk_event already stores every occurrence of a
 * series as a row of its own, which is exactly what counting days needs.
 *
 * Permanent bookings are skipped - a desk assigned indefinitely would otherwise report
 * every day in the window as a day in the office, which is not what the number means.
 */
export const officeDaySet = (events, { from, to }) => {
  const days = new Set()
  const windowFrom = from.startOf('day')
  const windowTo = to.startOf('day')

  events.forEach((event) => {
    if (!event.start || !event.end || event.end === INFINITY) return

    const endMoment = dayjs(event.end)
    let last = endMoment.startOf('day')
    // half-open intervals: an end at exactly midnight closes the previous day rather than
    // opening a new one, so 09:00-00:00 is one day in the office and not two
    if (endMoment.isSame(last)) last = last.subtract(1, 'day')
    if (last.isAfter(windowTo)) last = windowTo

    // the server filters by overlap, so an occurrence can reach outside the window on
    // either side; clipping keeps both ends inside the period being reported
    let cursor = dayjs(event.start).startOf('day')
    if (cursor.isBefore(windowFrom)) cursor = windowFrom

    while (!cursor.isAfter(last)) {
      days.add(cursor.format('YYYY-MM-DD'))
      cursor = cursor.add(1, 'day')
    }
  })

  return days
}

/** How many distinct days officeDaySet() finds. */
export const officeDays = (events, window) => officeDaySet(events, window).size

/**
 * Every day of the window, marked with whether it was spent in the office - what the
 * day strips on the counters draw. `days` is what officeDaySet() returns.
 */
export const dayStrip = (days, { from, to }) => {
  const strip = []
  const last = to.startOf('day')
  for (let cursor = from.startOf('day'); !cursor.isAfter(last); cursor = cursor.add(1, 'day')) {
    const date = cursor.format('YYYY-MM-DD')
    strip.push({ date, weekday: cursor.format('dd').charAt(0), label: cursor.format('ddd, MMM D'), on: days.has(date) })
  }
  return strip
}

/**
 * When the next booking is, in the words a person would use: "Tomorrow · Fri, Sep 26".
 *
 * Read on the viewer's own clock, like the time beside it. Only a single booking has one
 * date to name; a series says its rule instead (bookingDateLabel), since the grouped row
 * carries the first occurrence in the window and that may already be over today.
 */
export const bookingDayLabel = (start, now = dayjs()) => {
  const day = dayjs(start)
  const date = day.format('ddd, MMM D')
  const ahead = day.startOf('day').diff(now.startOf('day'), 'day')

  if (ahead === 0) return `Today · ${date}`
  if (ahead === 1) return `Tomorrow · ${date}`
  return date
}

/**
 * Splits the grouped rows the panel loads into the permanent desk and everything still
 * ahead, because the two read as different things: an assignment, and a diary.
 *
 * A row already in the past is dropped - the window opens at the start of today, so a
 * booking from this morning still comes back from the server.
 */
export const splitUpcoming = (rows, now = dayjs()) => {
  const permanent = rows.find((row) => row.kind === KIND_PERMANENT) || null
  const upcoming = rows
    .filter((row) => row.kind !== KIND_PERMANENT)
    .filter((row) => !isBookingPast(row, now))
    .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())

  return { permanent, upcoming }
}

/** "Sep 14 – 20", or "Aug 27 – Sep 25" when the period crosses a month. */
export const rangeLabel = ({ from, to }) => {
  const end = from.isSame(to, 'month') ? to.format('D') : to.format('MMM D')
  return `${from.format('MMM D')} – ${end}`
}

/**
 * A pending confirmation, as the booking it asks about.
 *
 * Always as a single booking, even when it belongs to a series: what is being asked about
 * is this occurrence, on this date, and the rule behind it would only crowd the question
 * out. That it is part of a series is said by the third button.
 */
export const toConfirmationRow = (item) => ({
  ...toBookingRow({ ...item, kind: KIND_SINGLE, key: `confirmation-${item.id}` }),
  id: item.id,
  setId: item.set_id,
  startsAt: item.start,
  answerBy: dayjs(item.expire_at).format('ddd, MMM D')
})

/**
 * Whether a confirmation asks about the booking shown.
 *
 * The confirmation names one occurrence; a grouped row starts at the first of its
 * occurrences in the window - so the same desk at the same instant is the same booking,
 * whether it is a one-off or the next day of a series.
 */
export const asksAbout = (confirmation, booking) =>
  Boolean(booking) &&
  booking.deskId === confirmation.deskId &&
  dayjs(booking.startsAt).isSame(dayjs(confirmation.startsAt))
